import {
  getLocalStorageItem,
  setLocalStorageItem,
  removeLocalStorageItem,
} from './utils/localStorageUtil';
import fetchAuthenticated from './fetchAuthenticated';
import getDate from './getDate';
import { isUserLoggedIn, getUserId } from './utils/auth';

const PERSONALISED_ADS_ANONYMOUS_CACHE_KEY = 'allow_personalised_ads_anonymous';
const PERSONALISED_ADS_LOGGED_IN_CACHE_KEY =
  'allow_personalised_ads_my_account';
const PERSONALISED_ADS_PREF_ID = 'allow_personalised_ads';

const allowPersonalisedAdsForAnonymousUser = () =>
  getLocalStorageItem(PERSONALISED_ADS_ANONYMOUS_CACHE_KEY) !== 'false';

const communicationsApiUrl = process.env.COMMUNICATIONS_API_URL;

const fetchCommunicationPreferences = () =>
  fetch(
    `${communicationsApiUrl}/resi_user/preferences/privacy?include_extra[]=${PERSONALISED_ADS_PREF_ID}`,
    { credentials: 'include' },
  );

const convertResponseToJson = (response) => {
  if (!response.ok) {
    throw Error(response.statusText);
  }

  return response.json();
};

const arePersonalisedAdsAllowed = (prefs) => {
  const allowPersonalisedAdsPref = prefs.find(
    (p) => p.identity === PERSONALISED_ADS_PREF_ID,
  );

  return !allowPersonalisedAdsPref || allowPersonalisedAdsPref.enabled;
};

const fetchMyAccountAllowPersonalisedAds = () =>
  fetchAuthenticated(fetchCommunicationPreferences)
    .then(convertResponseToJson)
    .then(arePersonalisedAdsAllowed);

const getDate24HoursLater = () =>
  new Date(getDate().getTime() + 24 * 60 * 60 * 1000);

const setMyAccountAllowPersonalisedAdsCache = (
  myAccountAllowPersonalisedAds,
) => {
  const allowPersonalisedAdsMyAccount = {
    value: myAccountAllowPersonalisedAds,
    expiry: getDate24HoursLater(),
    id: getUserId() || null,
  };

  setLocalStorageItem(
    PERSONALISED_ADS_LOGGED_IN_CACHE_KEY,
    JSON.stringify(allowPersonalisedAdsMyAccount),
  );
};

const cacheExistsAndNotExpired = (cache) =>
  cache && new Date(cache.expiry) > getDate();

const isNewLoginSession = (cache) => cache && cache.id !== getUserId();

let isfetching = false;

const allowPersonalisedAdsCache = () =>
  JSON.parse(getLocalStorageItem(PERSONALISED_ADS_LOGGED_IN_CACHE_KEY));

const outputErrorToConsole = (err) => console.error(err.message);

const removeTokenIfInvalid = () => {
  if (!isUserLoggedIn() || isNewLoginSession(allowPersonalisedAdsCache())) {
    removeLocalStorageItem(PERSONALISED_ADS_LOGGED_IN_CACHE_KEY);
  }
};

const allowPersonalisedAdsForLoggedInUser = () => {
  if (!isUserLoggedIn()) {
    return true;
  }

  const cache = allowPersonalisedAdsCache();

  if (cacheExistsAndNotExpired(cache)) {
    return cache.value;
  }

  if (!isfetching) {
    isfetching = true;
    fetchMyAccountAllowPersonalisedAds()
      .then(setMyAccountAllowPersonalisedAdsCache)
      .catch(outputErrorToConsole)
      .finally(() => {
        isfetching = false;
      });
  }

  return false;
};

const allowPersonalisedAds = () => {
  removeTokenIfInvalid();

  const allowAnonymous = allowPersonalisedAdsForAnonymousUser();

  if (!allowAnonymous) {
    return false;
  }

  return isUserLoggedIn()
    ? allowPersonalisedAdsForLoggedInUser()
    : allowAnonymous;
};

export default allowPersonalisedAds;
